/**
 * If you are not familiar with React Navigation, check out the "Fundamentals" guide:
 * https://reactnavigation.org/docs/getting-started
 *
 */
import * as React from 'react'
import { AppState, Alert, View, Text, useWindowDimensions, Image, ImageBackground, Pressable } from 'react-native'
import { NavigationContainer, DefaultTheme, DarkTheme, DrawerActions } from '@react-navigation/native'
import { createStackNavigator } from '@react-navigation/stack'
import { createDrawerNavigator, DrawerContentScrollView, DrawerItemList, DrawerItem } from '@react-navigation/drawer'
import { useMediaQuery } from 'react-responsive'
import { Modal, Spinner, Divider } from '@ui-kitten/components'
import tailwind from 'tailwind-rn'
import Icon from 'react-native-vector-icons/FontAwesome5'
import { useIsFetching } from 'react-query'
import * as Linking from 'expo-linking'
import { useQuery, useMutation, useQueryClient } from 'react-query'

import NotFoundScreen from '../screens/NotFoundScreen'
import Home from '../screens/Home'
import Quem from '../screens/Quem'
import Caravana from '../screens/Caravana'
import Conexo from '../screens/Conexo'
import Growth from '../screens/Growth'
import Capacitacao from '../screens/Capacitacao'
import MulherEmpresaria from '../screens/MulherEmpresaria'
import Perguntas from '../screens/PerguntasFrequentes'
import Fale from '../screens/FaleConosco'
import FormRD from '../screens/FormRD'
import FormRDMulher from '../screens/FormRDMulher'
import FormRDCaravanaVit from '../screens/FormRDCaravanaVit'
import FormRDCaravanaSsa from '../screens/FormRDCaravanaSsa'
import FormRDCaravanaFeira from '../screens/FormRDCaravanaFeira'
import FormRDConexo from '../screens/FormRDConexo'
import Privacidade from '../screens/Privacidade'

import LinkingConfiguration from './LinkingConfiguration'
import {setMasterNavigator} from './masterNavigator'

import { AppStorage } from '../services/app-storage.service'
import Colors from '../constants/Colors'

/*
 * Navigation theming: https://reactnavigation.org/docs/en/next/themes.html
 */
const navigatorTheme = {
  ...DefaultTheme,
  colors: {
    ...DefaultTheme.colors,
    // prevent layout blinking when performing navigation
    background: 'transparent',
  },
}

export const Navigation = (): React.ReactElement => {

  const [initialState, setInitialState] = React.useState()
  const routeNameRef = React.useRef<string>()
  const isFetching = useIsFetching()
  const windowWidth = useWindowDimensions().width
  const windowHeight = useWindowDimensions().height
  const appState = React.useRef(AppState.currentState)
  const [appStateVisible, setAppStateVisible] = React.useState(appState.current)
  const [jwtToken, setJwtToken] = React.useState()
  const [isReady, setIsReady] = React.useState(false)
  const queryClient = useQueryClient()
  const isLargeScreen = useMediaQuery({minDeviceWidth: 440})

  React.useEffect(() => {
    const restoreState = async () => {
      try {
        const initialUrl = await Linking.getInitialURL();

              queryClient.refetchQueries()
      } finally {
        setIsReady(true)
      }
    }

    if (!isReady) {
      restoreState();
    }
  }, [isReady])

  const _handleAppStateChange = async (nextAppState) => {
    if (
      appState.current.match(/inactive|background/) &&
      nextAppState === "active" 
    ) {
        //        navigate("Root", {})
    }

    appState.current = nextAppState;
    setAppStateVisible(appState.current);
  }

  React.useEffect(() => {

//    const messagingSubscriber = messaging().onMessage(async remoteMessage => {
//      setDialogTitle('Mensagem')
//      setDialogText(JSON.stringify(remoteMessage))
//      setShowDialog(true)
//    })

//    FingerprintScanner
//      .isSensorAvailable()
//      .then(biometryType => dispatch({type: 'SET_BIOMETRICS', biometrics: biometryType } ))
//      .catch(error => dispatch({type: 'SET_BIOMETRICS', biometrics: null } ))

    AppState.addEventListener("change", _handleAppStateChange)
    
    return () => {
//      messagingSubscriber
      AppState.removeEventListener("change", _handleAppStateChange)
    };

  }, [])

  if (!isReady) {
    return null
  }

  return (
    <NavigationContainer
      linking={LinkingConfiguration}
//      theme={navigatorTheme}
      ref={setMasterNavigator}
      onReady={() => routeNameRef.current = setMasterNavigator.current?.getCurrentRoute()?.name}
      initialState={initialState}
      onStateChange={(state) => {
        const previousRouteName = routeNameRef.current;
        const currentRouteName = setMasterNavigator.current?.getCurrentRoute()?.name
        AppStorage.setNavigation(JSON.stringify(state))
        if (previousRouteName !== currentRouteName) {
  //          Analytics.setCurrentScreen(currentRouteName);
        }

      // Save the current route name for later comparision
        routeNameRef.current = currentRouteName;
      }}
      documentTitle = {{
        enabled: false
      }}
    >
        <RootNavigator largeScreen={isLargeScreen} />
        <Modal
          visible={(isFetching) ? true : false}
          backdropStyle={{backgroundColor: 'rgba(0, 0, 0, 0.5)'}}
        >
          <Spinner size='giant' animating={(isFetching) ? true : false} />
        </Modal>
    </NavigationContainer>
  )
}

const RootStack = createDrawerNavigator();

export const RootNavigator = ({largeScreen}): React.ReactElement => (
    <RootStack.Navigator 
      screenOptions={{ 
        headerShown: false
      }}
      drawerStyle={{
        backgroundColor: Colors.roxo,
        width: largeScreen ? 400 : useWindowDimensions().width,
      }}
      drawerType = { largeScreen ? 'permanent' : 'back' }
      openByDefault={ largeScreen }
      drawerContent={props => <CustomDrawerContent largeScreen={largeScreen} {...props}></CustomDrawerContent>}
    >
        <RootStack.Screen 
          name="Root" 
          component={MainNavigator} 
        />
      <RootStack.Screen 
        name="NotFound" 
        component={NotFoundScreen} 
        options={{ title: 'Oops!' }} 
      />
    </RootStack.Navigator>
)



function CustomDrawerContent({ navigation, largeScreen }) {
  const windowWidth = largeScreen ? 400 : useWindowDimensions().width
  const windowHeight = useWindowDimensions().height

  const _go = (navigation, dest) => {
    navigation.dispatch(DrawerActions.toggleDrawer())
    navigation.navigate(dest)
  }
  
  const _goInstagram = () => {
    Linking.openURL('https://www.instagram.com/senacbahia')
  }
  
  const _goFacebook = () => {
    Linking.openURL('https://www.facebook.com/SenacBahia/')
  }
  
  const _goYoutube = () => {
    Linking.openURL('https://www.youtube.com/channel/UCybJWpVh2-GceJ0CnkyM9JA')
  }
  
  const _goZap = () => {
    Linking.openURL('https://api.whatsapp.com/send?phone=557131864000')
  }

  const drawerContent = () => (
    <DrawerContentScrollView style={tailwind('mt-6')}>
      <DrawerItem 
          icon={() => (<Icon name="home" color="#8a509e" style={{marginLeft: 10}} />)}
          label="PÁGINA INICIAL" 
          labelStyle={{color: 'white', fontWeight: 'bold'}} 
          activeBackgroundColor='#6c2385ff' 
          inactiveBackgroundColor='#6c2385ff' 
          style={{marginHorizontal: 20, borderRadius: 25}}
          onPress={() => _go(navigation, 'Home')} />
      <DrawerItem 
          icon={() => (<Icon name="users" color="#8a509e" style={{marginLeft: 10}} />)}
          label="QUEM SOMOS" 
          labelStyle={{color: 'white', fontWeight: 'bold'}} 
          activeBackgroundColor='#6c2385ff' 
          inactiveBackgroundColor='#6c2385ff' 
          style={{marginHorizontal: 20, borderRadius: 25}}
          onPress={() => _go(navigation, 'QuemSomos')} />
      <DrawerItem 
          icon={() => (<Icon name="chart-bar" color="#8a509e" style={{marginLeft: 10}} />)}
          label="CARAVANA GROWTH" 
          labelStyle={{color: 'white', fontWeight: 'bold'}} 
          activeBackgroundColor='#6c2385ff' 
          inactiveBackgroundColor='#6c2385ff' 
          style={{marginHorizontal: 20, borderRadius: 25}}
          onPress={() => _go(navigation, 'Caravana')} />
      <DrawerItem 
          icon={() => (<Icon name="chart-bar" color="#8a509e" style={{marginLeft: 10}} />)}
          label="CO.NEXO – PRÉ-INCUBADORA" 
          labelStyle={{color: 'white', fontWeight: 'bold'}} 
          activeBackgroundColor='#6c2385ff' 
          inactiveBackgroundColor='#6c2385ff' 
          style={{marginHorizontal: 20, borderRadius: 25}}
          onPress={() => _go(navigation, 'Conexo')} />
      <DrawerItem 
          icon={() => (<Icon name="chart-bar" color="#8a509e" style={{marginLeft: 10}} />)}
          label="PROGRAMA GROWTH" 
          labelStyle={{color: 'white', fontWeight: 'bold'}} 
          activeBackgroundColor='#6c2385ff' 
          inactiveBackgroundColor='#6c2385ff' 
          style={{marginHorizontal: 20, borderRadius: 25}}
          onPress={() => _go(navigation, 'Growth')} />
      <DrawerItem 
          icon={() => (<Icon name="chart-bar" color="#8a509e" style={{marginLeft: 10}} />)}
          label="CAPACITAÇÃO SOB MEDIDA" 
          labelStyle={{color: 'white', fontWeight: 'bold'}} 
          activeBackgroundColor='#6c2385ff' 
          inactiveBackgroundColor='#6c2385ff' 
          style={{marginHorizontal: 20, borderRadius: 25}}
          onPress={() => _go(navigation, 'Capacitacao')} />
      <DrawerItem 
          icon={() => (<Icon name="chart-bar" color="#8a509e" style={{marginLeft: 10}} />)}
          label="MULHER EMPRESÁRIA" 
          labelStyle={{color: 'white', fontWeight: 'bold'}} 
          activeBackgroundColor='#6c2385ff' 
          inactiveBackgroundColor='#6c2385ff' 
          style={{marginHorizontal: 20, borderRadius: 25}}
          onPress={() => _go(navigation, 'MulherEmpresaria')} />
          {/*
      <DrawerItem 
          icon={() => (<Icon name="star" color="#8a509e" style={{marginLeft: 10}} />)}
          label="CONTEÚDOS" 
          labelStyle={{color: 'white', fontWeight: 'bold'}} 
          activeBackgroundColor='#6c2385ff' 
          inactiveBackgroundColor='#6c2385ff' 
          style={{marginHorizontal: 20, borderRadius: 25}}
          onPress={() => navigation.dispatch(DrawerActions.toggleDrawer())} /> */}
      <DrawerItem 
          icon={() => (<Icon name="envelope" color="#8a509e" style={{marginLeft: 10}} />)}
          label="FALE CONOSCO" 
          labelStyle={{color: 'white', fontWeight: 'bold'}} 
          activeBackgroundColor='#6c2385ff' 
          inactiveBackgroundColor='#6c2385ff' 
          style={{marginHorizontal: 20, borderRadius: 25}}
          onPress={() => _go(navigation, 'FaleConosco')} />
          {/*
      <DrawerItem 
          icon={() => (<Icon name="search" color="#8a509e" style={{marginLeft: 10}} />)}
          label="PESQUISAR" 
          labelStyle={{color: 'white', fontWeight: 'bold'}} 
          activeBackgroundColor='#6c2385ff' 
          inactiveBackgroundColor='#6c2385ff' 
          style={{marginHorizontal: 20, borderRadius: 25}}
          onPress={() => navigation.dispatch(DrawerActions.toggleDrawer())} /> */}
      <DrawerItem 
          icon={() => (<Icon name="question" color="#8a509e" style={{marginLeft: 10}} />)}
          label="PERGUNTAS FREQUENTES" 
          labelStyle={{color: 'white', fontWeight: 'bold'}} 
          activeBackgroundColor='#6c2385ff' 
          inactiveBackgroundColor='#6c2385ff' 
          style={{marginHorizontal: 20, borderRadius: 25}}
          onPress={() => _go(navigation, 'PerguntasFrequentes')} />
    </DrawerContentScrollView>
  )

  if (!largeScreen) {
    return (
      <ImageBackground source={require('../assets/images/bg_drawer.svg')} style={{height: windowHeight, width: windowWidth}} resizeMode="cover">
      <View style={[tailwind('flex-row mx-6 justify-between'), { marginTop: 0 }]}>
      <Image source={require('../assets/images/logo_v.svg')} style={{width: windowWidth*0.20, height: windowHeight*0.25, marginTop: 0}} resizeMode="contain" />
      <View style={{width:1, marginBottom: 40, height: windowHeight*0.10, backgroundColor: 'white', marginTop: 50}} />
      <Image source={require('../assets/images/logos.svg')} style={{width: windowWidth*0.60, height: windowHeight*0.25, marginTop: 10, tintColor: "white"}} resizeMode="contain" />
    </View>
        <View style={{height:2, backgroundColor: 'white'}} />
        {drawerContent()}
      <View style={tailwind('flex-row mx-8 mb-2')}>
        <Pressable onPress={_goYoutube}><Image source={require('../assets/images/youtube_color.svg')} style={{width: windowWidth*0.12, height: windowHeight*0.12, marginRight: 40}} resizeMode="contain" /></Pressable>
        <Pressable onPress={_goInstagram}><Image source={require('../assets/images/instagram_color.svg')} style={{width: windowWidth*0.10, height: windowHeight*0.10, marginRight: 40, marginTop: 10}} resizeMode="contain" /></Pressable>
        <Pressable onPress={_goFacebook}><Image source={require('../assets/images/facebook_color.svg')} style={{width: windowWidth*0.060, height: windowHeight*0.060, marginRight: 40, marginTop: 30}} resizeMode="contain" /></Pressable>
      </View>
      </ImageBackground>  
  )
  } else {
    return (
    <View style={{backgroundColor: Colors.roxo, flex: 1, justifyContent: 'space-between'}}>
      <View style={[tailwind('mx-6'), { marginTop: 10 }]}>
        <Image source={require('../assets/images/logo_h.svg')} style={{width: windowWidth-100, height: 80, alignSelf: 'center'}} resizeMode="contain" />
      </View>
      <View style={{height:2, backgroundColor: 'white'}} />
      {drawerContent()}
    <View style={{backgroundColor: Colors.laranja_forte, height: 100}}>
      <View style={tailwind('flex-row mx-8 mb-2')}>
        <Pressable onPress={_goYoutube}><Image source={require('../assets/images/youtube_color.svg')} style={{width: windowWidth*0.12, height: windowHeight*0.12, marginRight: 40}} resizeMode="contain" /></Pressable>
        <Pressable onPress={_goInstagram}><Image source={require('../assets/images/instagram_color.svg')} style={{width: windowWidth*0.10, height: windowHeight*0.10, marginRight: 40, marginTop: 10}} resizeMode="contain" /></Pressable>
        <Pressable onPress={_goFacebook}><Image source={require('../assets/images/facebook_color.svg')} style={{width: windowWidth*0.060, height: windowHeight*0.060, marginRight: 40, marginTop: 30}} resizeMode="contain" /></Pressable>
      </View>
    </View>
    </View>
  )
    }
}

const MainStack = createStackNavigator();

function MainNavigator() {
  return (
    <MainStack.Navigator
      screenOptions={({ navigation }) => ({
        headerShown: false,
        headerStyle: {
          backgroundColor: "#d5d0d0",
        },

      })}>
      <MainStack.Screen 
        name="Home" 
        component={Home} 
        options={{
          title: 'Soluções Empreendedoras SENAC',
        }}
      />
      <MainStack.Screen 
        name="QuemSomos" 
        component={Quem} 
        options={{
          title: 'Quem Somos',
        }}
      />
      <MainStack.Screen 
        name="Caravana" 
        component={Caravana} 
        options={{
          title: 'Caravana Growth',
        }}
      />
      <MainStack.Screen 
        name="Conexo" 
        component={Conexo} 
        options={{
          title: 'Programa CO.NEXO – Pré-incubadora de Negócios',
        }}
      />
      <MainStack.Screen 
        name="Growth" 
        component={Growth} 
        options={{
          title: 'Growth Hacking',
        }}
      />
      <MainStack.Screen 
        name="Capacitacao" 
        component={Capacitacao} 
        options={{
          title: 'Capacitação Sob Medida',
        }}
      />
      <MainStack.Screen 
        name="MulherEmpresaria" 
        component={MulherEmpresaria} 
        options={{
          title: 'Certificação da Mulher Empresária',
        }}
      />
      <MainStack.Screen 
        name="PerguntasFrequentes" 
        component={Perguntas} 
        options={{
          title: 'Perguntas Frequentes',
        }}
      />
      <MainStack.Screen 
        name="FaleConosco" 
        component={Fale} 
        options={{
          title: 'Fale Conosco',
        }}
      />
      <MainStack.Screen 
        name="FormRD" 
        component={FormRD} 
        options={{
          title: 'Inscrição',
        }}
      />
      <MainStack.Screen 
        name="FormRDCaravanaVit" 
        component={FormRDCaravanaVit} 
        options={{
          title: 'Inscrição',
        }}
      />
      <MainStack.Screen 
        name="FormRDCaravanaFeira" 
        component={FormRDCaravanaFeira} 
        options={{
          title: 'Inscrição',
        }}
      />
      <MainStack.Screen 
        name="FormRDCaravanaSsa" 
        component={FormRDCaravanaSsa} 
        options={{
          title: 'Inscrição',
        }}
      />
      <MainStack.Screen 
        name="FormRDMulher" 
        component={FormRDMulher} 
        options={{
          title: 'Inscrição',
        }}
      />
      <MainStack.Screen 
        name="FormRDConexo" 
        component={FormRDConexo} 
        options={{
          title: 'Inscrição',
        }}
      />
      <MainStack.Screen 
        name="Privacidade" 
        component={Privacidade} 
        options={{
          title: 'Política de Privacidade',
        }}
      />
    </MainStack.Navigator>
  )
}
